import React from "react";
import {
  edgePathAt,
  rightAnchorAt,
  leftAnchorAt,
} from "../../helper/masterSuiteCanvas";

// Colour tokens live in workflow.css as CSS custom properties so nothing here is
// a hardcoded hex; the arrowhead marker uses context-stroke so it always matches
// its own cable's colour.
const STATUS_STROKE = {
  pass: "var(--ms-pass)",
  fail: "var(--ms-fail)",
  partial: "var(--ms-warn)",
  running: "var(--ms-accent)",
  stopped: "var(--ms-muted)",
  skipped: "var(--ms-muted)",
};

// The whole cable layer: one absolutely-positioned <svg> behind the nodes.
// pointer-events:none so clicks fall through to the nodes / insert buttons.
//
// Takes the laid-out geometry rather than the raw node list: the parent computed it
// once, and recomputing here could drift from where the nodes were actually drawn.
function WorkflowConnector({ edges = [], bounds, mode = "view", onEdgeHover }) {
  if (!edges.length || !bounds) return null;
  const w = bounds.width;
  const h = bounds.height;

  return (
    <svg
      className="ms-canvas-cables"
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
    >
      <defs>
        {/* markerUnits="userSpaceOnUse" decouples the arrow from strokeWidth. The
            default MULTIPLIES by it, so "7" was rendering at 14px and parking the
            chevron in the gap in front of the card. refX=10 puts the TIP — not the
            body — at the path end, so the arrow terminates flush on the node edge. */}
        <marker
          id="ms-cable-arrow"
          viewBox="0 0 10 10"
          refX="10"
          refY="5"
          markerUnits="userSpaceOnUse"
          markerWidth="8"
          markerHeight="8"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
        </marker>
      </defs>

      {edges.map(({ from, to }) => {
        let stroke = "var(--ms-cable)";
        let opacity = 1;

        if (mode === "result") {
          // Muting is per-CABLE, decided by the two steps it actually joins — not by
          // "everything after the first stopped node", which was only ever right for a
          // single straight line. With fan-out, a failure on one branch must leave a
          // sibling branch's cables fully lit.
          const st = from.status || "";
          if (to.status === "stopped" || st === "stopped") {
            stroke = "var(--ms-muted)";
            opacity = 0.4;
          } else {
            stroke = STATUS_STROKE[st] || "var(--ms-cable)";
          }
        }

        const a = rightAnchorAt(from);
        const b = leftAnchorAt(to);
        return (
          <g key={`${from.key}->${to.key}`} opacity={opacity}>
            {/* Invisible hit target so the CABLE itself can be hovered. The whole layer is
                pointer-events:none, but a child may opt back in — and "stroke" means only
                the 18px band along the cable is hoverable, not its bounding box, so two
                crossing branches never claim each other's hover. */}
            {onEdgeHover && (
              <path
                d={edgePathAt(from, to)}
                fill="none"
                stroke="transparent"
                strokeWidth={18}
                style={{ pointerEvents: "stroke", cursor: "pointer" }}
                onMouseEnter={() => onEdgeHover(`${from.key}->${to.key}`)}
              />
            )}
            {/* Always solid. The dash used to mean "stop-if-failed is OFF", but that
                defaults off, so a whole suite came out dashed and read as provisional
                rather than as a signal. NOTE: the node card carries no stop-if-failed
                badge, so nothing on the canvas encodes it now. */}
            <path
              d={edgePathAt(from, to)}
              fill="none"
              stroke={stroke}
              strokeWidth={2}
              markerEnd="url(#ms-cable-arrow)"
            />
            {/* Anchor dots — the cable visibly plugs into each card. 3px reads as a
                plug beside a 2px stroke; the old 4px was a blob next to it. */}
            <circle cx={a.x} cy={a.y} r={3} fill={stroke} />
            <circle cx={b.x} cy={b.y} r={3} fill={stroke} />
          </g>
        );
      })}
    </svg>
  );
}

export default WorkflowConnector;
